import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAdminAgents } from "@/queries/adminAgents";

import { AddInstanceDialog } from "./AddInstanceDialog";
import { FamilyCard } from "./FamilyCard";

export function AdminAgentsPage() {
  const { t } = useTranslation("admin-agents");
  const { data, isPending, isError } = useAdminAgents();
  const [addOpen, setAddOpen] = useState(false);

  if (isPending) {
    return (
      <PageShell onAdd={() => setAddOpen(true)} canAdd={false}>
        <p className="text-sm text-muted-foreground">{t("page.loading")}</p>
      </PageShell>
    );
  }
  if (isError || !data) {
    return (
      <PageShell onAdd={() => setAddOpen(true)} canAdd={false}>
        <Alert variant="destructive">
          <AlertDescription>{t("page.loadError")}</AlertDescription>
        </Alert>
      </PageShell>
    );
  }
  if (data.families.length === 0) {
    return (
      <PageShell onAdd={() => setAddOpen(true)} canAdd={false}>
        <p className="text-sm text-muted-foreground">{t("page.empty")}</p>
      </PageShell>
    );
  }

  return (
    <PageShell onAdd={() => setAddOpen(true)} canAdd>
      <div className="flex flex-col gap-4">
        {data.families.map((family) => (
          <FamilyCard
            key={family.skill}
            family={family}
            instances={data.instances}
          />
        ))}
      </div>

      <AddInstanceDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        families={data.families}
        instances={data.instances}
      />
    </PageShell>
  );
}

function PageShell({
  children,
  onAdd,
  canAdd,
}: {
  children: React.ReactNode;
  onAdd: () => void;
  canAdd: boolean;
}) {
  const { t } = useTranslation("admin-agents");
  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("page.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("page.subtitle")}</p>
        </div>
        <Button
          onClick={onAdd}
          disabled={!canAdd}
          data-testid="add-instance-open"
        >
          <Plus className="h-4 w-4" />
          {t("page.addInstance")}
        </Button>
      </header>
      {children}
    </div>
  );
}
